import { useEffect, useMemo } from 'react'
import {
  IMMEDIATE_ACTION_OPTIONS,
  FINDINGS_TEMPLATE,
  isRecaptureAction
} from './helpers'
import {
  Container,
  Header,
  HeaderRow,
  Title,
  HeaderBadge,
  Description,
  Section,
  SectionTitle,
  TwoColumns,
  Column,
  Label,
  Select,
  TextInput,
  TextArea,
  EyeCard,
  EyeTitle,
  Hint,
  Divider,
  WarningMessage
} from './styles'

const STUDY_EYES = {
  0: { right: true, left: true },
  1: { right: true, left: false },
  2: { right: false, left: true }
}

const toBoolean = (value) => value === true || value === 'true'
const isCompletedSelection = (value) => value !== '' && value !== null && value !== undefined
const getSelectValue = (value) => (value === null || value === undefined ? '' : String(value))

export function OphthalmologyReportForm ({
  formData,
  onFormChange,
  setEmptyField,
  activeStudyTab,
  studyType,
  readOnly = false
}) {
  const eyeAvailability = STUDY_EYES[studyType] || STUDY_EYES[0]
  const isRightEyeTabActive = activeStudyTab === 0
  const isLeftEyeTabActive = activeStudyTab === 1
  const isClinicalTabActive = activeStudyTab === 2

  const canEditRightEye = eyeAvailability.right && isRightEyeTabActive
  const canEditLeftEye = eyeAvailability.left && isLeftEyeTabActive

  const getReadOnlyValue = (value) => {
    if (value === null || value === undefined || value === '') return '-'
    return value
  }

  const tabHintText = useMemo(() => {
    if (readOnly) return 'Vista en modo solo lectura del informe ya informado.'
    if (isClinicalTabActive) return 'Seleccioná una pestaña de ojo en el panel derecho para editar Hallazgos.'
    return 'Solo se habilita el campo del ojo seleccionado en el panel derecho.'
  }, [isClinicalTabActive, readOnly])

  const isRecapture = isRecaptureAction(formData.accion_inmediata)

  const hasStructuredDataLoaded = useMemo(() => {
    return isCompletedSelection(formData.referible) ||
      isCompletedSelection(formData.grado_RD) ||
      isCompletedSelection(formData.sospecha_MD) ||
      isCompletedSelection(formData.otras_patologias) ||
      (formData.hallazgos_ojo_derecho && formData.hallazgos_ojo_derecho.trim() !== FINDINGS_TEMPLATE.trim()) ||
      (formData.hallazgos_ojo_izquierdo && formData.hallazgos_ojo_izquierdo.trim() !== FINDINGS_TEMPLATE.trim()) ||
      Boolean(formData.impresion_diagnostica?.trim())
  }, [formData])

  useEffect(() => {
    if (!setEmptyField) return
    if (readOnly) {
      setEmptyField(false)
      return
    }

    if (isRecapture) {
      const hasConductaText = Boolean(formData.conducta_recomendaciones?.trim())
      setEmptyField(!hasConductaText)
      return
    }

    const hasRequiredSelections = [
      formData.referible,
      formData.grado_RD,
      formData.sospecha_MD,
      formData.otras_patologias,
      formData.accion_inmediata
    ].every(isCompletedSelection)

    const hasRequiredTexts = Boolean(formData.impresion_diagnostica?.trim()) &&
      Boolean(formData.conducta_recomendaciones?.trim())

    const isDefaultRight = formData.hallazgos_ojo_derecho?.trim() === FINDINGS_TEMPLATE.trim()
    const isDefaultLeft = formData.hallazgos_ojo_izquierdo?.trim() === FINDINGS_TEMPLATE.trim()

    const hasRequiredRightEye = !eyeAvailability.right || (Boolean(formData.hallazgos_ojo_derecho?.trim()) && !isDefaultRight)
    const hasRequiredLeftEye = !eyeAvailability.left || (Boolean(formData.hallazgos_ojo_izquierdo?.trim()) && !isDefaultLeft)

    const isIncomplete = !hasRequiredSelections || !hasRequiredTexts || !hasRequiredRightEye || !hasRequiredLeftEye
    setEmptyField(isIncomplete)
  }, [
    formData.referible,
    formData.grado_RD,
    formData.sospecha_MD,
    formData.otras_patologias,
    formData.accion_inmediata,
    formData.impresion_diagnostica,
    formData.conducta_recomendaciones,
    formData.hallazgos_ojo_derecho,
    formData.hallazgos_ojo_izquierdo,
    eyeAvailability.right,
    eyeAvailability.left,
    readOnly,
    setEmptyField
  ])

  const updateField = (fieldName, value) => {
    if (readOnly) return
    if (!onFormChange) return
    onFormChange((previousData) => ({
      ...previousData,
      [fieldName]: value
    }))
  }

  const handleSelectChange = (event) => {
    if (readOnly) return
    const { name, value } = event.target

    if (name === 'referible' || name === 'otras_patologias') {
      if (value === '') {
        onFormChange((previousData) => ({
          ...previousData,
          [name]: '',
          ...(name === 'otras_patologias'
            ? { patologia_concomitante_detalle: '' }
            : {})
        }))
        return
      }

      const boolValue = toBoolean(value)
      onFormChange((previousData) => ({
        ...previousData,
        [name]: boolValue,
        ...(name === 'otras_patologias' && !boolValue
          ? { patologia_concomitante_detalle: '' }
          : {})
      }))
      return
    }

    if (name === 'grado_RD' || name === 'sospecha_MD') {
      if (value === '') {
        updateField(name, '')
        return
      }
      updateField(name, Number.parseInt(value, 10))
      return
    }

    updateField(name, value)
  }

  const handleFindingsBlur = (fieldName) => {
    if (readOnly) return
    const currentValue = (formData[fieldName] || '').trim()
    if (currentValue) return
    updateField(fieldName, '')
  }

  return (
    <Container>
      <Header>
        <HeaderRow>
          <Title>Informe profesional</Title>
          <HeaderBadge>{readOnly ? 'Solo lectura' : 'Manual'}</HeaderBadge>
        </HeaderRow>
        <Description>
          {readOnly
            ? 'Esta vista es solo lectura. Podés revisar el informe y descargar el PDF.'
            : 'Completá el informe acá revisando las imágenes/datos clínicos de la derecha.'}
        </Description>
      </Header>

      {isRecapture && hasStructuredDataLoaded && (
        <WarningMessage>
          Advertencia: Has seleccionado una acción de recaptura. Los datos clínicos estructurados que ya cargaste se limpiarán al enviar este informe.
        </WarningMessage>
      )}

      {!isRecapture && (
        <>
          <Section>
            <SectionTitle>Clasificación principal</SectionTitle>
        <TwoColumns>
          <Column>
            <Label htmlFor='referible'>¿Está bien referido?</Label>
            <Select
              id='referible'
              name='referible'
              value={getSelectValue(formData.referible)}
              onChange={handleSelectChange}
              disabled={readOnly}
            >
              <option value='' disabled>Seleccionar</option>
              <option value='true'>Sí</option>
              <option value='false'>No</option>
            </Select>
          </Column>

          <Column>
            <Label htmlFor='grado_RD'>Grado de RD observada</Label>
            <Select
              id='grado_RD'
              name='grado_RD'
              value={getSelectValue(formData.grado_RD)}
              onChange={handleSelectChange}
              disabled={readOnly}
            >
              <option value='' disabled>Seleccionar</option>
              <option value='0'>0 - Ausencia de retinopatía diabética</option>
              <option value='1'>1 - Retinopatía diabética no proliferativa leve</option>
              <option value='2'>2 - Retinopatía diabética no proliferativa moderada</option>
              <option value='3'>3 - Retinopatía diabética no proliferativa severa</option>
              <option value='4'>4 - Retinopatía diabética proliferativa</option>
            </Select>
          </Column>

          <Column>
            <Label htmlFor='sospecha_MD'>Sospecha de edema macular diabético</Label>
            <Select
              id='sospecha_MD'
              name='sospecha_MD'
              value={getSelectValue(formData.sospecha_MD)}
              onChange={handleSelectChange}
              disabled={readOnly}
            >
              <option value='' disabled>Seleccionar</option>
              <option value='0'>Baja</option>
              <option value='1'>Moderada</option>
              <option value='2'>Alta</option>
            </Select>
          </Column>

          <Column>
            <Label htmlFor='otras_patologias'>Se observan patologías concomitantes</Label>
            <Select
              id='otras_patologias'
              name='otras_patologias'
              value={getSelectValue(formData.otras_patologias)}
              onChange={handleSelectChange}
              disabled={readOnly}
            >
              <option value='' disabled>Seleccionar</option>
              <option value='true'>Sí</option>
              <option value='false'>No</option>
            </Select>
          </Column>
        </TwoColumns>

        {formData.otras_patologias && (
          <Column>
            <Label htmlFor='patologia_concomitante_detalle'>Patología concomitante observada</Label>
            <TextInput
              id='patologia_concomitante_detalle'
              name='patologia_concomitante_detalle'
              placeholder='Ej: sospecha de glaucoma, DMAE, etc.'
              value={formData.patologia_concomitante_detalle || ''}
              onChange={(event) => updateField('patologia_concomitante_detalle', event.target.value)}
              disabled={readOnly}
            />
          </Column>
        )}
      </Section>

      <Divider />

      <Section>
        <SectionTitle>Hallazgos</SectionTitle>
        <Hint>{tabHintText}</Hint>
        <TwoColumns>
          <EyeCard $active={canEditLeftEye}>
            <EyeTitle $disabled={!canEditLeftEye}>Ojo izquierdo</EyeTitle>
            {!eyeAvailability.left && <Hint>No aplica para este tipo de estudio.</Hint>}
            <TextArea
              value={readOnly ? getReadOnlyValue(formData.hallazgos_ojo_izquierdo) : (formData.hallazgos_ojo_izquierdo || '')}
              onChange={(event) => updateField('hallazgos_ojo_izquierdo', event.target.value)}
              onBlur={() => handleFindingsBlur('hallazgos_ojo_izquierdo')}
              disabled={readOnly || !canEditLeftEye}
              $active={canEditLeftEye}
            />
          </EyeCard>

          <EyeCard $active={canEditRightEye}>
            <EyeTitle $disabled={!canEditRightEye}>Ojo derecho</EyeTitle>
            {!eyeAvailability.right && <Hint>No aplica para este tipo de estudio.</Hint>}
            <TextArea
              value={readOnly ? getReadOnlyValue(formData.hallazgos_ojo_derecho) : (formData.hallazgos_ojo_derecho || '')}
              onChange={(event) => updateField('hallazgos_ojo_derecho', event.target.value)}
              onBlur={() => handleFindingsBlur('hallazgos_ojo_derecho')}
              disabled={readOnly || !canEditRightEye}
              $active={canEditRightEye}
            />
          </EyeCard>
        </TwoColumns>
      </Section>

      <Section>
        <SectionTitle>Impresión diagnóstica</SectionTitle>
        <TextArea
          value={readOnly ? getReadOnlyValue(formData.impresion_diagnostica) : (formData.impresion_diagnostica || '')}
          onChange={(event) => updateField('impresion_diagnostica', event.target.value)}
          disabled={readOnly}
          $active
          $compact
          placeholder='Ingrese la impresión diagnóstica'
        />
      </Section>
      </>
      )}

      <Section>
        <SectionTitle>Conducta / recomendaciones</SectionTitle>
        <Column>
          <Label htmlFor='accion_inmediata'>Acción inmediata recomendada</Label>
          <Select
            id='accion_inmediata'
            name='accion_inmediata'
            value={formData.accion_inmediata || ''}
            onChange={handleSelectChange}
            disabled={readOnly}
          >
            <option value='' disabled>Seleccionar</option>
            {IMMEDIATE_ACTION_OPTIONS.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </Select>
        </Column>

        <Column>
          <Label htmlFor='conducta_recomendaciones'>Detalle de conducta / recomendación posterior</Label>
          <TextArea
            id='conducta_recomendaciones'
            value={readOnly ? getReadOnlyValue(formData.conducta_recomendaciones) : (formData.conducta_recomendaciones || '')}
            onChange={(event) => updateField('conducta_recomendaciones', event.target.value)}
            disabled={readOnly}
            $active
            $compact
            placeholder='Ingrese la conducta recomendada'
          />
        </Column>
      </Section>
    </Container>
  )
}

export default OphthalmologyReportForm
